import { retryWithBackoff } from '@blufin/react-core';
import { Hmac, HttpRequestMetadata, PayloadCipherContext, RequestMetadataCodec, WeakEncryptor } from '@blufin/security';
import type { ApiConfig, HttpMethod, IHttpClient, IHttpClientOptions, QueryParamsType } from '@blufin/types';
import {
  CONTENT_TYPE_JSON,
  HEADER_CONTENT_TYPE,
  HEADER_HMAC_CONTENT_LENGTH,
  HEADER_HMAC_SIGNATURE,
  HEADER_HMAC_TIMESTAMP,
  HEADER_REQUEST_META,
  HTTP_REQUEST_TIMEOUT_MS,
  ServiceAuthMethod
} from '@blufin/types';

interface HmacCache {
  secret: string;
  timestampOffset: number;
}

/**
 * Lightweight browser-compatible HTTP client for public (HMAC-only) requests.
 * Implements IHttpClient so it can be provided via HttpContextAlpha,
 * enabling useHttpClientBeta() to work unchanged in Next.js sites.
 * Features over the original HttpClientPublic:
 * - Retry with exponential backoff for transient failures
 * - 401 HMAC refresh: on 401 for HMAC requests, clears cached token and retries once
 */
export class HttpClientPublicAlpha implements IHttpClient {
  private hmacCache: Map<string, HmacCache> = new Map();

  /**
   * Creates a new HttpClientPublicAlpha instance.
   * @param apiConfig - Configuration mapping service names to their base URLs.
   */
  constructor(private readonly apiConfig: ApiConfig) {}

  /**
   * Makes a GET request.
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param options - Optional request options.
   */
  async get<T = any>(service: string, authMethod: string, path: string, options?: IHttpClientOptions): Promise<T> {
    return this._requestWithRetry<T>('GET', service, authMethod, path, undefined, options);
  }

  /**
   * Makes a POST request.
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param payload - Optional request body payload.
   * @param options - Optional request options.
   */
  async post<T = any>(
    service: string,
    authMethod: string,
    path: string,
    payload?: any,
    options?: IHttpClientOptions
  ): Promise<T> {
    return this._requestWithRetry<T>('POST', service, authMethod, path, payload, options);
  }

  /**
   * Makes a PUT request.
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param payload - The request body payload.
   * @param options - Optional request options.
   */
  async put<T = any>(
    service: string,
    authMethod: string,
    path: string,
    payload: any,
    options?: IHttpClientOptions
  ): Promise<T> {
    return this._requestWithRetry<T>('PUT', service, authMethod, path, payload, options);
  }

  /**
   * Makes a PATCH request.
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param payload - The request body payload.
   * @param options - Optional request options.
   */
  async patch<T = any>(
    service: string,
    authMethod: string,
    path: string,
    payload: any,
    options?: IHttpClientOptions
  ): Promise<T> {
    return this._requestWithRetry<T>('PATCH', service, authMethod, path, payload, options);
  }

  /**
   * Makes a DELETE request.
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param options - Optional request options.
   */
  async delete<T = any>(service: string, authMethod: string, path: string, options?: IHttpClientOptions): Promise<T> {
    return this._requestWithRetry<T>('DELETE', service, authMethod, path, undefined, options);
  }

  /** No-op — public client has no auth tokens. */
  setAuthData(): void {
    /* no-op */
  }

  /** Terminates the HTTP client and clears cached data. */
  terminate(): void {
    this.hmacCache.clear();
  }

  /** Clears the HMAC token cache (eg: on wake from sleep). */
  clearHmacCache(): void {
    this.hmacCache.clear();
  }

  /**
   * Wraps request() with retry logic and 401 HMAC refresh.
   * @param method - The HTTP method.
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param payload - Optional request body payload.
   * @param options - Optional request options.
   */
  private async _requestWithRetry<T>(
    method: string,
    service: string,
    authMethod: string,
    path: string,
    payload?: any,
    options?: IHttpClientOptions
  ): Promise<T> {
    const retryConfig = options?.retry ? { ...options.retry } : {};
    let hasRetriedFor401 = false;
    return retryWithBackoff.execute<T>(async () => {
      try {
        return await this._request<T>(method, service, authMethod, path, payload, options);
      } catch (err: unknown) {
        const statusCode =
          typeof err === 'object' && err !== null ? (err as { statusCode?: number }).statusCode : undefined;
        /** On 401 for HMAC requests, clear cache and retry once */
        if (statusCode === 401 && authMethod === ServiceAuthMethod.HMAC && !hasRetriedFor401) {
          hasRetriedFor401 = true;
          const serviceConfig = this.apiConfig[service];
          if (serviceConfig) this.hmacCache.delete(serviceConfig.baseUrl);
          return this._request<T>(method, service, authMethod, path, payload, options);
        }
        throw err;
      }
    }, retryConfig);
  }

  /**
   * Internal method that handles all HTTP requests.
   * @param method - The HTTP method (GET, POST, etc.).
   * @param service - The service identifier from apiConfig.
   * @param authMethod - The authentication method to use.
   * @param path - The API endpoint path.
   * @param payload - Optional request body payload.
   * @param options - Optional request options.
   */
  private async _request<T>(
    method: string,
    service: string,
    authMethod: string,
    path: string,
    payload?: any,
    options?: IHttpClientOptions
  ): Promise<T> {
    const serviceConfig = this.apiConfig[service];
    if (!serviceConfig)
      throw new Error(`Unknown service: ${service}. Available services: ${Object.keys(this.apiConfig).join(', ')}`);
    const url = this._buildUrl(serviceConfig.baseUrl, path, options?.params);
    const headers: Record<string, string> = {
      [HEADER_CONTENT_TYPE]: CONTENT_TYPE_JSON,
      ...options?.headers
    };
    if (authMethod === ServiceAuthMethod.HMAC) {
      const hmacHeaders = await this._computeHmacHeaders(
        serviceConfig.baseUrl,
        method as HttpMethod,
        options?.params,
        payload
      );
      Object.assign(headers, hmacHeaders);
    }
    if (options?.encrypt) {
      const requestMeta = new HttpRequestMetadata();
      if (options.encrypt.request) requestMeta.markPayloadEncrypted();
      if (options.encrypt.response) requestMeta.requestEncryptedResponse();
      headers[HEADER_REQUEST_META] = RequestMetadataCodec.encodeRequest(requestMeta);
    }
    if (options?.encrypt?.request && payload) {
      const hmacData = this.hmacCache.get(serviceConfig.baseUrl);
      if (hmacData) payload = WeakEncryptor.encryptPayload(hmacData.secret, PayloadCipherContext.REQUEST, payload);
    }
    const requestInit: globalThis.RequestInit = { headers, method };
    if (payload !== undefined && method !== 'GET') requestInit.body = JSON.stringify(payload);
    const timeout = options?.timeout ?? HTTP_REQUEST_TIMEOUT_MS;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestInit.signal = controller.signal;
    try {
      const response = await fetch(url, requestInit);
      clearTimeout(timeoutId);
      const contentType = response.headers.get(HEADER_CONTENT_TYPE.toLowerCase());
      let data: any;
      if (contentType?.includes(CONTENT_TYPE_JSON)) {
        const text = await response.text();
        data = text.trim() ? JSON.parse(text) : null;
      } else data = await response.text();
      const responseMetaHeader = response.headers.get(HEADER_REQUEST_META);
      if (response.ok && responseMetaHeader && data?.data) {
        const hmacData = this.hmacCache.get(serviceConfig.baseUrl);
        if (hmacData) {
          const responseMeta = RequestMetadataCodec.decodeResponse({ [HEADER_REQUEST_META]: responseMetaHeader });
          if (responseMeta?.isEncrypted)
            data = {
              ...data,
              data: WeakEncryptor.decryptPayload(hmacData.secret, PayloadCipherContext.RESPONSE, data.data)
            };
        }
      }
      if (!response.ok) throw { statusCode: response.status, ...((typeof data === 'object' && data) || { data }) };
      return data as T;
    } catch (err: unknown) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError')
        throw {
          message: `Request timed out after ${timeout}ms`,
          name: 'TimeoutError',
          statusCode: 408,
          timeoutMs: timeout
        };
      throw err;
    }
  }

  /**
   * Builds the full URL with query parameters.
   * @param baseUrl - The base URL of the service.
   * @param path - The API endpoint path.
   * @param params - Optional query parameters.
   */
  private _buildUrl(baseUrl: string, path: string, params?: QueryParamsType): string {
    const cleanBase = baseUrl.replace(/\/$/, '');
    const cleanPath = path.replace(/^\//, '');
    let url = `${cleanBase}/${cleanPath}`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) value.forEach(v => searchParams.append(key, String(v)));
        else if (value !== undefined && value !== null) searchParams.append(key, String(value));
      }
      const queryString = searchParams.toString();
      if (queryString) url += `?${queryString}`;
    }
    return url;
  }

  /**
   * Computes HMAC authentication headers.
   * @param baseUrl - The base URL of the service (used to look up HMAC token).
   * @param method - The HTTP method.
   * @param queryParams - Optional query parameters to include in the signature.
   * @param payload - Optional request body to include in the signature.
   */
  private async _computeHmacHeaders(
    baseUrl: string,
    method: HttpMethod,
    queryParams?: QueryParamsType,
    payload?: any
  ): Promise<Record<string, string>> {
    let hmacData = this.hmacCache.get(baseUrl);
    if (!hmacData) {
      hmacData = await this._fetchHmacToken(baseUrl);
      this.hmacCache.set(baseUrl, hmacData);
    }
    const { contentLength, hash, timestamp } = Hmac.compute(
      hmacData.secret,
      {
        body: payload,
        method: method.toUpperCase() as HttpMethod,
        queryParams: queryParams && Object.keys(queryParams).length > 0 ? queryParams : undefined
      },
      hmacData.timestampOffset
    );
    return {
      [HEADER_HMAC_SIGNATURE]: hash,
      [HEADER_HMAC_TIMESTAMP]: timestamp.toString(),
      [HEADER_HMAC_CONTENT_LENGTH]: contentLength.toString()
    };
  }

  /**
   * Fetches the HMAC token from the service.
   * @param baseUrl - The base URL of the service.
   */
  private async _fetchHmacToken(baseUrl: string): Promise<HmacCache> {
    const hmacUrl = `${baseUrl}/v1/hmac`;
    const response = await fetch(hmacUrl, { headers: { [HEADER_CONTENT_TYPE]: CONTENT_TYPE_JSON }, method: 'GET' });
    if (!response.ok) throw new Error(`Failed to fetch HMAC token: ${response.status} ${response.statusText}`);
    const result = (await response.json()) as { data: { hmacToken: string; timestamp: string } };
    if (!result.data?.hmacToken || !result.data?.timestamp)
      throw new Error('Invalid HMAC response: missing hmacToken or timestamp');
    const serverTime = new Date(result.data.timestamp).getTime();
    const timestampOffset = serverTime - Date.now();
    return { secret: result.data.hmacToken, timestampOffset };
  }
}
